import { useEffect, useState } from 'react';
import { ClipboardText, QrCode, Warning, CheckCircle, Lightning } from '@phosphor-icons/react';
import { getAsset, getAssetNetwork, NETWORKS, compareAmounts, subAmounts, type AssetCode, type NetworkId, type WithdrawPreviewDTO } from '@onyx/shared';
import { useWallet, useMoneyMutation, postWithdrawPreview } from '../lib/queries';
import { availableOf } from '../lib/app';
import { useLang, useT, errorText } from '../lib/i18n';
import { cleanAmountInput, formatCrypto } from '../lib/format';
import { navigate, switchTab } from '../lib/router';
import { standardTag } from '../lib/networks';
import { api, ApiError } from '../lib/api';
import { canScanQr, haptic, pasteText, scanQr } from '../lib/telegram';
import { AssetList, NetworkList } from '../components/Pickers';
import { AssetChip } from '../components/AssetChip';
import { HoldButton } from '../components/HoldButton';
import { Facts, Field, Notice, Screen, ScreenHeader, Slab } from '../components/ui';

export function WithdrawPickAsset() {
  const t = useT();
  const wallet = useWallet();
  return (
    <Screen>
      <ScreenHeader title={t['withdraw.title']} sub={t['pick.asset']} />
      <AssetList wallet={wallet.data} filter={(c) => Number(availableOf(wallet.data, c)) > 0} onPick={(code) => navigate(`/withdraw/${code}`)} />
    </Screen>
  );
}

export function WithdrawPickNetwork({ asset }: { asset: AssetCode }) {
  const t = useT();
  const def = getAsset(asset)!;
  useEffect(() => {
    if (def.networks.length === 1) navigate(`/withdraw/${asset}/${def.networks[0]!.network}`, { replace: true });
  }, [asset, def]);
  return (
    <Screen>
      <ScreenHeader title={`${t['withdraw.title']} ${asset}`} sub={t['pick.network']} />
      <NetworkList asset={asset} mode="withdraw" onPick={(n) => navigate(`/withdraw/${asset}/${n.network}`)} />
    </Screen>
  );
}

/** Strip payment-URI wrappers a scanned QR may carry (ton://transfer/…, ethereum:…, bitcoin:…?amount=). */
function addressFromScan(raw: string): string {
  const s = raw.trim();
  const m = s.match(/^[a-z]+:(?:\/\/transfer\/)?([^?@/]+)/i);
  return m ? m[1]! : s;
}

export function WithdrawForm({ asset, network }: { asset: AssetCode; network: NetworkId }) {
  const t = useT();
  const lang = useLang();
  const wallet = useWallet();
  const def = getAsset(asset)!;
  const an = getAssetNetwork(asset, network)!;
  const net = NETWORKS[network];
  const available = availableOf(wallet.data, asset);

  const [address, setAddress] = useState('');
  const [memo, setMemo] = useState('');
  const [amount, setAmount] = useState('');
  const [preview, setPreview] = useState<WithdrawPreviewDTO | null>(null);
  const [step, setStep] = useState<'form' | 'review' | 'done'>('form');
  const [result, setResult] = useState<{ internal: boolean; status: string } | null>(null);
  const submit = useMoneyMutation((v: { asset: string; network: string; address: string; amount: string; memo: string | null }) =>
    api<{ id: string; internal: boolean; status: string }>('/withdraw', { body: v }),
  );
  const [error, setError] = useState<string | null>(null);

  const trimmed = address.trim();
  useEffect(() => {
    setPreview(null);
    if (!trimmed || !amount || Number(amount) <= 0) return;
    const id = setTimeout(() => {
      postWithdrawPreview({ asset, network, address: trimmed, amount, memo: memo || null })
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 280);
    return () => clearTimeout(id);
  }, [asset, network, trimmed, amount, memo]);

  const internal = preview?.internalRecipient ?? null;
  const fee = internal ? '0' : an.withdrawFee;
  const maxAmount = compareAmounts(available, fee) > 0 ? subAmounts(available, fee, def.decimals) : '0';
  const addressInvalid = preview ? !preview.addressValid : false;
  const belowMin = !internal && amount && Number(amount) > 0 && compareAmounts(amount, an.minWithdraw) < 0;
  const tooMuch = amount && compareAmounts(amount, maxAmount) > 0;
  const ready = !!preview && preview.addressValid && !belowMin && !tooMuch && Number(amount) > 0;

  const amountError = tooMuch ? t['withdraw.notEnough'] : belowMin ? t['withdraw.belowMin'](`${formatCrypto(an.minWithdraw, 8, lang)} ${asset}`) : null;
  const recipientName = internal ? (internal.username ? `@${internal.username}` : internal.firstName) : '';

  if (step === 'done' && result) {
    return (
      <Screen className="done" footer={<Slab onClick={() => switchTab('/history')}>{t['common.done']}</Slab>}>
        <div className="done__body">
          <span className="done__mark">
            <CheckCircle size={56} weight="fill" aria-hidden="true" />
          </span>
          <h1>{t['withdraw.doneTitle']}</h1>
          <p className="num">
            {formatCrypto(amount, def.decimals, lang)} {asset} → {result.internal ? recipientName : `${trimmed.slice(0, 6)}…${trimmed.slice(-6)}`}
          </p>
          <p className="done__note">{result.internal ? t['withdraw.internalDone'] : result.status === 'review' ? t['withdraw.reviewBody'] : t['withdraw.doneBody']}</p>
        </div>
      </Screen>
    );
  }

  if (step === 'review' && preview) {
    return (
      <Screen
        className="review"
        footer={
          <HoldButton
            label={t['withdraw.hold']}
            busyLabel={t['withdraw.sending']}
            busy={submit.isPending}
            onConfirm={() =>
              submit.mutate(
                { asset, network, address: trimmed, amount, memo: memo.trim() || null },
                {
                  onSuccess: (r) => {
                    haptic.success();
                    setResult(r);
                    setStep('done');
                  },
                  onError: (e) => {
                    haptic.error();
                    setError(errorText(t, e instanceof ApiError ? e.code : undefined));
                    setStep('form');
                  },
                },
              )
            }
          />
        }
      >
        <ScreenHeader title={t['withdraw.confirmTitle']} onBack={() => setStep('form')} />
        <div className="review__hero">
          <AssetChip asset={asset} size={48} />
          <p className="review__amount num">
            {formatCrypto(preview.receive, def.decimals, lang)} <span>{asset}</span>
          </p>
        </div>
        <div className="review__address">
          <span className="review__label">{internal ? recipientName : t['withdraw.address']}</span>
          <span className="review__value">
            {trimmed}
          </span>
        </div>
        <Facts
          items={[
            { label: t['withdraw.network'], value: `${net.name}${standardTag(an.standard) ? ` · ${an.standard}` : ''}` },
            memo.trim() && { label: t['withdraw.memo'], value: memo.trim() },
            { label: t['withdraw.fee'], value: internal ? t['common.free'] : <span className="num">{formatCrypto(preview.fee, 9, lang)} {asset}</span> },
            { label: t['withdraw.total'], value: <span className="num">{formatCrypto(preview.total, 9, lang)} {asset}</span>, strong: true },
          ]}
        />
        {!internal && (
          <Notice tone="warn" icon={<Warning size={18} weight="bold" />}>
            {t['withdraw.irreversible']}
          </Notice>
        )}
      </Screen>
    );
  }

  return (
    <Screen
      className="withdraw"
      footer={
        <Slab disabled={!ready} onClick={() => setStep('review')}>
          {t['withdraw.review']}
        </Slab>
      }
    >
      <ScreenHeader title={`${t['withdraw.title']} ${asset}`} sub={`${net.name}${standardTag(an.standard) ? ` · ${an.standard}` : ''}`} />
      {error && <Notice tone="bad">{error}</Notice>}

      <Field
        label={t['withdraw.address']}
        htmlFor="wd-address"
        error={addressInvalid ? t['withdraw.invalidAddress'](net.name) : null}
        aside={
          <span className="field__tools">
            <button
              type="button"
              className="tool-btn"
              onClick={() =>
                void pasteText().then((v) => {
                  if (v) setAddress(v.trim());
                })
              }
            >
              <ClipboardText size={16} aria-hidden="true" /> {t['withdraw.paste']}
            </button>
            {canScanQr() && (
              <button
                type="button"
                className="tool-btn"
                onClick={() =>
                  void scanQr(t['withdraw.scan']).then((v) => {
                    if (v) setAddress(addressFromScan(v));
                  })
                }
              >
                <QrCode size={16} aria-hidden="true" /> {t['withdraw.scan']}
              </button>
            )}
          </span>
        }
      >
        <textarea
          id="wd-address"
          className="input input--address"
          rows={2}
          value={address}
          spellCheck={false}
          autoCapitalize="off"
          autoComplete="off"
          placeholder={t['withdraw.addressPh']}
          onChange={(e) => setAddress(e.target.value.replace(/\s+/g, ''))}
        />
      </Field>

      {internal && (
        <Notice tone="good" icon={<Lightning size={18} weight="fill" />}>
          {t['withdraw.internal'](recipientName)}
        </Notice>
      )}

      {net.memo && (
        <Field label={t['withdraw.memo']} htmlFor="wd-memo">
          <input id="wd-memo" className="input" value={memo} maxLength={120} placeholder={t['withdraw.memoPh']} onChange={(e) => setMemo(e.target.value)} />
        </Field>
      )}

      <Field
        label={t['withdraw.amount']}
        htmlFor="wd-amount"
        error={amountError}
        hint={
          <span className="num">
            {t['common.balance']}: {formatCrypto(available, def.decimals, lang)} {asset}
          </span>
        }
      >
        <div className="input-group">
          <input
            id="wd-amount"
            className="input input--amount num"
            inputMode="decimal"
            value={lang === 'ru' ? amount.replace('.', ',') : amount}
            placeholder="0"
            onChange={(e) => setAmount(cleanAmountInput(e.target.value, def.decimals))}
          />
          <span className="input-group__unit">{asset}</span>
          <button type="button" className="tool-btn tool-btn--solid" onClick={() => setAmount(maxAmount)}>
            {t['withdraw.max']}
          </button>
        </div>
      </Field>

      <Facts
        items={[
          { label: t['withdraw.fee'], value: internal ? t['common.free'] : <span className="num">{formatCrypto(an.withdrawFee, 9, lang)} {asset}</span> },
          { label: t['withdraw.receive'], value: <span className="num">{amount ? formatCrypto(amount, def.decimals, lang) : '0'} {asset}</span>, strong: true },
        ]}
      />
    </Screen>
  );
}
